import React, { useState } from 'react';
import { stripeConnectApi } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

const CreatePlan: React.FC<{ businessId: string, facilityId: string }> = ({ businessId, facilityId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const { user } = useAuth();
    
    const [formData, setFormData] = useState({
        name: '',
        interval: 'month' as 'month' | 'year',
        price: '',
        initiationFee: '',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!businessId || !facilityId) {
            setError('No facility ID found');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const priceInCents = Math.round(parseFloat(formData.price) * 100);
            
            await stripeConnectApi.createPlan(
                businessId,
                facilityId,
                formData.name,
                formData.interval,
                priceInCents,
                Math.round(parseFloat(formData.initiationFee || '0') * 100)
            );
            
            setSuccess(true);
            setFormData({
                name: '',
                interval: 'month',
                price: '',
                initiationFee: '',
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create plan');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Create Subscription Plan</h2>
            
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
                    {error}
                </div>
            )}
            
            {success && (
                <div className="mb-4 p-4 bg-green-100 text-green-700 rounded">
                    Plan created successfully!
                </div>
            )}
            
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                        Plan Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="interval">
                        Billing Interval
                    </label>
                    <select
                        id="interval"
                        name="interval"
                        value={formData.interval}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="month">Monthly</option>
                        <option value="year">Yearly</option>
                    </select>
                </div>
                
                <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="price">
                        Price (USD)
                    </label>
                    <input
                        type="number"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        required
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="initiationFee">
                        Initiation Fee (USD)
                    </label>
                    <input
                        type="number"
                        id="initiationFee"
                        name="initiationFee"
                        value={formData.initiationFee}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                
                <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                        loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                >
                    {loading ? 'Creating...' : 'Create Plan'}
                </button>
            </form>
        </div>
    );
};

export default CreatePlan; 